import { ChangeEvent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { faCirclePlus } from '@fortawesome/pro-regular-svg-icons/faCirclePlus';
import { faChartNetwork } from '@fortawesome/pro-solid-svg-icons/faChartNetwork';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { IconCheckbox } from 'us-react-components/dist/IconCheckbox/IconCheckbox';
import { Affiliation, ParentAffiliationService } from 'us-web-services';
import { useDependencies } from '../../../../../dependencies';
import { Storage } from '../../../../../constants';
import { Button, ButtonType } from '../../../../Button';

type Props = {
  affiliations: Affiliation[];
  navigateToExploreGroupsPage(): void;
}

type AffiliationOptions = {
  id: number;
  name: string;
  selected: boolean;
  parentAffiliationId?: number;
}

export const GroupsNullState = ({ affiliations, navigateToExploreGroupsPage }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { stores, services: { tracker } } = useDependencies();
  const { user } = stores.user;
  const { parent } = stores.parent;
  const [affiliationOptions, setAffiliationOptions] = useState<AffiliationOptions[]>([]);

  useEffect(() => {
    const allAffiliations = affiliations.map(aff => ({ id: aff.id ?? 0, name: aff.name ?? '', selected: false }));
    setAffiliationOptions(allAffiliations);
  }, [affiliations]);

  const handleTracking = async (event: string, trackingProps: {elementText: string}): Promise<void> => tracker.track(event, trackingProps);

  const handleCheckboxChange = async (e: ChangeEvent<HTMLInputElement>, affiliation: AffiliationOptions): Promise<void> => {
    affiliation.selected = e.currentTarget.checked;

    if (affiliation.selected) {
      const config = {
        parentId: user.id,
        affiliation: {
          id: affiliation.id,
        }
      };

      const response = (await ParentAffiliationService.create(config)).data;
      if (response.data?.id) {
        affiliation.parentAffiliationId = response.data.id;
        parent.affiliations?.push(response.data);
      } else {
        affiliation.selected = false;
      }
    } else if (affiliation.parentAffiliationId && !affiliation.selected) {
      await ParentAffiliationService.delete(affiliation.parentAffiliationId);
      parent.affiliations = parent.affiliations?.filter(o => o.affiliation?.id !== affiliation.id);
    }

    const affiliationsArr = affiliationOptions;
    setAffiliationOptions([...affiliationsArr]);
    localStorage.setItem(Storage.US_PARENT_OBJECT, JSON.stringify(parent));
  };

  return (
    <div className='h-fit mb-5 xl:mb-24'>
      <div className='w-full md:w-2/3 mb-10'>
        <div className='block mb-3 mt-4'>
          <FontAwesomeIcon icon={faChartNetwork} className='text-bubblegum mr-2 text-2xl' />
          <span className='font-bold text-xl'>{t('dashboard.widgets.myGroups.nullStateTitle')}</span>
        </div>
        <div className='block text-sm mb-3'>
          {t('dashboard.widgets.myGroups.nullStateDescription')}
        </div>
      </div>
      <div>
        <div className='font-bold mb-4'>{t('dashboard.widgets.myGroups.nullStateSubtitle')}</div>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
          {affiliationOptions.map(affiliation => (
            <IconCheckbox
              key={`affiliation-${affiliation.id}`}
              label={affiliation.name}
              name={affiliation.name}
              selected={affiliation.selected}
              selectedIcon={faCircleCheck}
              selectedIconClasses='text-sapphire'
              setSelected={(e): Promise<void> => handleCheckboxChange(e, affiliation)}
              trackingCallback={handleTracking}
              unSelectedIcon={faCirclePlus}
              unSelectedIconClasses='text-black'
            />
          ))}
        </div>
      </div>
      <div className='mt-8'>
        <Button
          buttonType={ButtonType.tertiary}
          onClick={navigateToExploreGroupsPage}
        >
          {t('dashboard.widgets.myGroups.exploreMoreGroups')}
          <FontAwesomeIcon icon={faAngleRight} className='pl-2 m-[-1px]' />
        </Button>
      </div>
    </div>
  );
};
